import React, { useState, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { Upload, message, Dropdown, Modal, Statistic, Card } from "antd"
import {
  UploadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  StarOutlined,
  StarFilled
} from "@ant-design/icons"

import * as api from "../../../../api"

import { useStateValue } from "../../../_shared/context/AppStateStore"
import useRestTable from "../../../_shared/hooks/useRestTable.js"

import AppButton from "../../../_shared/components/AppButton"

import Table from "../../../_shared/components/Table"

// ----------------------------------------------------------------------------

function Dashboard(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const containerRef = useRef(null)
  const [isImportLoading, setIsImportLoading] = useState(false)
  const [messageApi, contextHolder] = message.useMessage(); // New message API
  const [modal, modalContextHolder] = Modal.useModal(); // New modal API

  const [{ token }] = useStateValue()

  const [
    productsList,
    fetchProducts,
    isProductsLoading,
    pagination,
    totalProducts,
  ] = useRestTable("/products/", token)

  const [productCodesList, fetchProductCodes, isCodesLoading, , totalCodes] =
    useRestTable("/product-codes", token)

  const [, fetchCategories, , , totalCategories] = useRestTable(
    "/categories",
    token
  )

  // -------------------------------------
  // Effects
  // -------------------------------------

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function getPaginationObject() {
    return {
      size: "small",
      total: totalProducts,
      current: pagination.current,
      pageSize: pagination.pageSize,
      showSizeChanger: false,
    }
  }

  function handleTableChange(pagination) {
    fetchProducts({ pagination })
  }

  async function handleImportProducts(file) {
    try {
      await api.importProductsFromCSV(file, token)

      fetchProducts()
      fetchCategories()

      messageApi.success("Prodotti importati correttamente.")
    } catch (error) {
      messageApi.error("Si è verificato un errore nell'import.")
    }
  }

  async function handleImportProductCodes(file) {
    try {
      await api.importProductCodesFromCSV(file, token)

      fetchProductCodes()

      messageApi.success("Codici importati correttamente.")
    } catch (error) {
      messageApi.error("Si è verificato un errore nell'import.")
    }
  }

  // -------------------------------------

  function showProductsImportConfirmModal(importFeedback, file) {
    modal.confirm({
      title: "Confermi di voler importare i prodotti?",
      icon: (
        <ExclamationCircleOutlined style={{ fontSize: 32, color: "#1890ff" }} />
      ),
      content: renderProductsFeedbackModalContent(importFeedback),
      okText: "Importa",
      cancelText: "Annulla",
      onOk: () => handleImportProducts(file),
      onCancel: () => { },
      getContainer: () => document.body,
    })
  }

  function showCodesImportConfirmModal(importFeedback, file) {
    modal.confirm({
      title: "Confermi di voler importare i codici?",
      icon: (
        <ExclamationCircleOutlined style={{ fontSize: 32, color: "#1890ff" }} />
      ),
      content: renderCodesFeedbackModalContent(importFeedback),
      okText: "Importa",
      cancelText: "Annulla",
      onOk: () => handleImportProductCodes(file),
      onCancel: () => { },
      getContainer: () => document.body,
    })
  }

  async function handleProductUploadFeedback(request) {
    setIsImportLoading(true)

    const { onSuccess } = request

    const file = request.file

    try {
      const importFeedback = await api.getProductImportSideEffects(file, token)

      showProductsImportConfirmModal(importFeedback, file)

      setIsImportLoading(false)

      // tells to antd upload that everything is fine
      onSuccess("ok")
    } catch (error) {
      console.error(error)
      setIsImportLoading(false)
      messageApi.error("Non è stato possibile avviare la procedura di import.")
    }
  }

  async function handleProductCodesUploadFeedback(request) {
    setIsImportLoading(true)

    const { onSuccess } = request

    const file = request.file

    try {
      const importFeedback = await api.getProductCodesImportSideEffects(
        file,
        token
      )

      showCodesImportConfirmModal(importFeedback, file)

      setIsImportLoading(false)

      // tells to antd upload that everything is fine
      onSuccess("ok")
    } catch (error) {
      console.error(error)
      setIsImportLoading(false)
      messageApi.error("Non è stato possibile avviare la procedura di import.")
    }
  }

  async function handleDosageDataImport(request) {
    setIsImportLoading(true)

    try {
      const { onSuccess } = request

      const file = request.file

      await api.importDosageData(file, token)

      setIsImportLoading(false)

      messageApi.success("Dosaggi importati con successo.")

      // tells to antd upload that everything is fine
      onSuccess("ok")
    } catch (error) {
      setIsImportLoading(false)
      messageApi.error("Non è stato possibile avviare la procedura di import.")
    }
  }

  async function handleProductImagesImport(request) {
    console.log(">>> handleProductImagesImport: ", request)
  }

  async function handleTargetsImagesUpload(request) {
    console.log(">>> handleTargetsImagesUpload: ", request)
  }

  function handleAssetUpload(relativeEndpoint) {
    return async function (request) {
      const { onSuccess } = request

      const file = request.file

      try {
        await api.importAssetFile(file, relativeEndpoint, token)

        messageApi.success(`${file.name} importato con successo.`)

        // tells to antd upload that everything is fine
        onSuccess("ok")
      } catch (error) {
        messageApi.error(`Non è stato possibile importare il file ${file.name}.`)
      }
    }
  }

  // -------------------------------------

  function renderProductsFeedbackModalContent(importFeedback) {
    return (
      <div>
        <p
          style={{ marginBottom: 20, marginLeft: 10 }}
        >{`Con l'import verranno applicate le seguenti operazioni.`}</p>

        <div className="feedback-list">
          <div style={{ marginBottom: 10 }}>
            <CheckCircleOutlined
              style={{ marginRight: 10, color: "#52c41a", fontSize: 18 }}
            />
            <span>
              <strong>{importFeedback.productsWillBeCreated}</strong> nuovi
              prodotti verranno creati.
            </span>
          </div>
          <div style={{ marginBottom: 10 }}>
            <WarningOutlined
              style={{ marginRight: 10, color: "#faad14", fontSize: 18 }}
            />
            <span>
              <strong>{importFeedback.productsWillBeUpdated}</strong> prodotti
              verranno aggiornati.
            </span>
          </div>

          <div style={{ marginBottom: 10 }}>
            <DeleteOutlined
              style={{ marginRight: 10, color: "#f81d22", fontSize: 18 }}
            />
            <span>
              <strong>{importFeedback.productsWillBeDeleted}</strong> prodotti
              verranno eliminati dal database.
            </span>
          </div>
        </div>
      </div>
    )
  }

  function renderCodesFeedbackModalContent(importFeedback) {
    return (
      <div>
        <p
          style={{ marginBottom: 20, marginLeft: 10 }}
        >{`Con l'import verranno applicate le seguenti operazioni.`}</p>

        <div className="feedback-list">
          <div style={{ marginBottom: 10 }}>
            <CheckCircleOutlined
              style={{ marginRight: 10, color: "#52c41a", fontSize: 18 }}
            />
            <span>
              <strong>{importFeedback.codesWillBeCreated}</strong> nuovi codici
              verranno creati.
            </span>
          </div>
          <div style={{ marginBottom: 10 }}>
            <WarningOutlined
              style={{ marginRight: 10, color: "#faad14", fontSize: 18 }}
            />
            <span>
              <strong>{importFeedback.codesWillBeUpdated}</strong> codici
              verranno aggiornati.
            </span>
          </div>

          <div style={{ marginBottom: 10 }}>
            <DeleteOutlined
              style={{ marginRight: 10, color: "#f81d22", fontSize: 18 }}
            />
            <span>
              <strong>{importFeedback.codesWillBeDeleted}</strong> codici
              verranno eliminati dal database.
            </span>
          </div>
        </div>
      </div>
    )
  }


  const items = [
    {
      key: 'csv',
      label: 'CSV',
      children: [
        {
          key: 'products-import',
          label: 'Prodotti',
          onClick: () => {
            const uploadProps = {
              name: "file",
              accept: ".csv",
              showUploadList: false,
              multiple: false,
              customRequest: handleProductUploadFeedback,
            };
            // Create a file input element and trigger it
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = uploadProps.accept;
            input.multiple = uploadProps.multiple;
            input.onchange = (e) => {
              const file = e.target.files[0];
              if (file) {
                const request = {
                  file,
                  onSuccess: () => { },
                };
                uploadProps.customRequest(request);
              }
            };
            input.click();
          },
        },
        {
          key: 'codes-import',
          label: 'Codici',
          onClick: () => {
            const uploadProps = {
              name: "file",
              accept: ".csv",
              showUploadList: false,
              multiple: false,
              customRequest: handleProductCodesUploadFeedback,
            };
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = uploadProps.accept;
            input.multiple = uploadProps.multiple;
            input.onchange = (e) => {
              const file = e.target.files[0];
              if (file) {
                const request = {
                  file,
                  onSuccess: () => { },
                };
                uploadProps.customRequest(request);
              }
            };
            input.click();
          },
        },
        {
          key: 'dosages-import',
          label: 'Dati dosaggio',
          onClick: () => {
            const uploadProps = {
              name: "file",
              accept: ".csv",
              showUploadList: false,
              multiple: false,
              customRequest: handleDosageDataImport,
            };
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = uploadProps.accept;
            input.multiple = uploadProps.multiple;
            input.onchange = (e) => {
              const file = e.target.files[0];
              if (file) {
                const request = {
                  file,
                  onSuccess: () => { },
                };
                uploadProps.customRequest(request);
              }
            };
            input.click();
          },
        },
      ],
    },
    {
      key: 'images',
      label: 'Immagini',
      children: [
        {
          key: 'product-images-import',
          label: 'Prodotti',
          onClick: () => {
            const uploadProps = {
              name: "file",
              accept: "image/*",
              showUploadList: false,
              multiple: true,
              customRequest: handleAssetUpload("products"),
            };
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = uploadProps.accept;
            input.multiple = uploadProps.multiple;
            input.onchange = (e) => {
              const files = e.target.files;
              if (files.length > 0) {
                Array.from(files).forEach(file => {
                  const request = {
                    file,
                    onSuccess: () => { },
                  };
                  uploadProps.customRequest(request);
                });
              }
            };
            input.click();
          },
        },
      ],
    },
    {
      key: 'documents',
      label: 'Documenti',
      children: [
        {
          key: 'product-security-sheets-import',
          label: 'Schede di sicurezza',
          onClick: () => {
            const uploadProps = {
              name: "file",
              accept: "application/pdf",
              showUploadList: false,
              multiple: true,
              customRequest: handleAssetUpload("security-sheets"),
            };
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = uploadProps.accept;
            input.multiple = uploadProps.multiple;
            input.onchange = (e) => {
              const files = e.target.files;
              if (files.length > 0) {
                Array.from(files).forEach(file => {
                  const request = {
                    file,
                    onSuccess: () => { },
                  };
                  uploadProps.customRequest(request);
                });
              }
            };
            input.click();
          },
        },
        {
          key: 'product-data-sheets-import',
          label: 'Schede tecniche',
          onClick: () => {
            const uploadProps = {
              name: "file",
              accept: "application/pdf",
              showUploadList: false,
              multiple: true,
              customRequest: handleAssetUpload("data-sheets"),
            };
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = uploadProps.accept;
            input.multiple = uploadProps.multiple;
            input.onchange = (e) => {
              const files = e.target.files;
              if (files.length > 0) {
                Array.from(files).forEach(file => {
                  const request = {
                    file,
                    onSuccess: () => { },
                  };
                  uploadProps.customRequest(request);
                });
              }
            };
            input.click();
          },
        },
        {
          key: 'targets-import',
          label: 'Etichette',
          onClick: () => {
            const uploadProps = {
              name: "file",
              accept: "application/pdf",
              showUploadList: false,
              multiple: true,
              customRequest: handleAssetUpload("artworks"),
            };
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = uploadProps.accept;
            input.multiple = uploadProps.multiple;
            input.onchange = (e) => {
              const files = e.target.files;
              if (files.length > 0) {
                Array.from(files).forEach(file => {
                  const request = {
                    file,
                    onSuccess: () => { },
                  };
                  uploadProps.customRequest(request);
                });
              }
            };
            input.click();
          },
        },
      ],
    },
  ];


  async function toggleProductFeatured(product) {
    const body = {
      featured: !product.featured
    }

    await api.updateResource("/products", token, product.id, body)

    fetchProducts()
  }

  function renderProductActions(product) {

    const { featured } = product

    const Icon = featured ? StarFilled : StarOutlined
    return <Icon size={44} style={{ fontSize: 24, color: featured ? "gold" : "#252525" }} onClick={() => toggleProductFeatured(product)} />
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  return (
    <div ref={containerRef} className={props.className}>
      {contextHolder}
      {modalContextHolder}

      <div className="top-bar">
        <Dropdown.Button
          menu={{ items }}
          trigger={["click"]}
          getPopupContainer={() => document.body}
        >
          Importa
          {/* <AppButton loading={isImportLoading}>
            {!isImportLoading && <UploadOutlined />} Importa
          </AppButton> */}
        </Dropdown.Button>
      </div>

      <div className="flex-row">
        <Card className="flex-item">
          <Statistic title="Prodotti" value={totalProducts || 0} precision={0} />
        </Card>
        <Card className="flex-item">
          <Statistic title="Codici" value={totalCodes || 0} precision={0} />
        </Card>
        <Card className="flex-item">
          <Statistic title="Categorie" value={totalCategories || 0} precision={0} />
        </Card>
      </div>

      <div className="grid-row">
        <Table
          className="table"
          columns={getProductsColumns(renderProductActions)}
          rowKey={(record) => record.vebiId}
          dataSource={productsList}
          loading={isProductsLoading}
          pagination={getPaginationObject()}
          onChange={handleTableChange}
        />
        <Table
          className="table"
          columns={getCodesColumns()}
          dataSource={productCodesList}
          rowKey={(record) => `${record?.code}-${record?.product?.vebiId}`}
          loading={isCodesLoading}
          pagination={getPaginationObject()}
          onChange={handleTableChange}
        />
      </div>
    </div>
  )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

Dashboard.propTypes = {
  className: PropTypes.string.isRequired,
}

Dashboard.defaultProps = {}

// ----------------------------------------------------------------------------

const StyledDashboard = styled(Dashboard)`
  & {
    .top-bar {
      margin-bottom: 20px;

      .inner-item {
        margin-left: 10px;
      }
    }

    .flex-row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;

      width: 100%;

      .flex-item {
        width: 32%;
      }
    }

    .grid-row {
      margin-top: 25px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      .table {
        width: 49%;

        .ant-table {
          height: fit-content;
        }
      }
    }
  }
`
// ----------------------------------------------------------------------------

export default StyledDashboard

// ----------------------------------------------------------------------------
// Table columns
// ----------------------------------------------------------------------------

function getProductsColumns(renderActions) {
  return [
    {
      title: "Nome commerciale",
      dataIndex: "commercialName",
      key: "commercialName",
    },
    {
      title: "Categoria",
      dataIndex: "category",
      key: "category",
      render: (category) => {
        return category && category.name
      },
    },
    {
      "title": "",
      key: "actions",
      dataIndex: "",
      render: (_, record) => {
        return renderActions(record)
      }
    }
    // {
    //   title: "Sotto categoria",
    //   dataIndex: "subCategory",
    //   key: "subCategory",
    //   render: (category) => {
    //     return category && category.name
    //   },
    // },
    // {
    //   title: "",
    //   dataIndex: "image",
    //   key: "image",
    //   //eslint-disable-next-line
    //   render: (image) => {
    //     return image && <img src={image} height={60} />
    //   },
    // },
  ]
}

// -------------------------------------

function getCodesColumns() {
  return [
    {
      title: "Codice",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Nome commerciale",
      dataIndex: "product",
      key: "commercialName",
      render: (product, record, index) => product && product.commercialName,
    },
  ]
}